/**
 * FloatingTextRenderer — temporary world-space drift messages
 * (ELIMINATED, CONSUMED, LAUNCHED!).
 *
 * Extracted from HUDRenderer as a composition class.
 */

import { Container, Text, TextStyle } from 'pixi.js';
import { HUD_LAYOUT, Z, VOID, SKY_DEATH } from '../../constants';

interface FloatingText {
  text: Text;
  startY: number;
  life: number;
  maxLife: number;
  driftDown: boolean;
}

export class FloatingTextRenderer {
  private worldContainer: Container;
  private floatingTexts: FloatingText[] = [];

  constructor(worldContainer: Container) {
    this.worldContainer = worldContainer;
  }

  showEliminated(x: number, y: number, color: number): void {
    const style = new TextStyle({
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: HUD_LAYOUT.ELIMINATED_FONT_SIZE,
      fontWeight: 'bold',
      fill: color,
      align: 'center',
      stroke: { color: 0x000000, width: 3 },
    });
    const text = new Text({ text: 'ELIMINATED', style });
    text.anchor.set(0.5, 0.5);
    text.position.set(x, y - 60);
    text.zIndex = Z.TANKS + 5;
    this.worldContainer.addChild(text);

    this.floatingTexts.push({
      text,
      startY: y - 60,
      life: 2.0,
      maxLife: 2.0,
      driftDown: false,
    });
  }

  showVoidEliminated(x: number, y: number): void {
    const style = new TextStyle({
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: HUD_LAYOUT.CONSUMED_FONT_SIZE,
      fontWeight: 'bold',
      fill: VOID.EDGE_COLOR,
      align: 'center',
      stroke: { color: 0x000000, width: 3 },
    });
    const text = new Text({ text: 'CONSUMED', style });
    text.anchor.set(0.5, 0.5);
    text.position.set(x, y - 40);
    text.zIndex = Z.TANKS + 5;
    this.worldContainer.addChild(text);

    this.floatingTexts.push({
      text,
      startY: y - 40,
      life: 2.0,
      maxLife: 2.0,
      driftDown: true,
    });
  }

  showSkyEliminated(x: number, y: number, color: number): void {
    const style = new TextStyle({
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: HUD_LAYOUT.LAUNCHED_FONT_SIZE,
      fontWeight: 'bold',
      fill: SKY_DEATH.TEXT_COLOR,
      align: 'center',
      stroke: { color: 0x000000, width: 3 },
    });
    const text = new Text({ text: 'LAUNCHED!', style });
    text.anchor.set(0.5, 0.5);
    text.position.set(x, y - 60);
    text.zIndex = Z.TANKS + 5;
    this.worldContainer.addChild(text);

    this.floatingTexts.push({
      text,
      startY: y - 60,
      life: 2.0,
      maxLife: 2.0,
      driftDown: false,
    });
  }

  update(dt: number): void {
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      ft.life -= dt;

      if (ft.life <= 0) {
        this.worldContainer.removeChild(ft.text);
        ft.text.destroy();
        this.floatingTexts.splice(i, 1);
        continue;
      }

      const t = ft.life / ft.maxLife;
      ft.text.alpha = t;
      ft.text.position.y = ft.startY + (ft.driftDown ? 1 : -1) * (1 - t) * 40;
    }
  }

  destroy(): void {
    for (const ft of this.floatingTexts) {
      ft.text.destroy();
    }
    this.floatingTexts = [];
  }
}
